import { Reveal } from "@/lib/motion";
import { motion } from "framer-motion";

const STEPS = [
  {
    n: "01",
    title: "Look",
    body:
      "We start with a Business Systems Review — an honest look at how your business runs today, where time is leaking, and where structure is missing.",
  },
  {
    n: "02",
    title: "Reimagine",
    body:
      "We map a clearer way of working — offers, communication, workflow and the role AI can play in your day-to-day. Practical, not theoretical.",
  },
  {
    n: "03",
    title: "Build",
    body:
      "We help implement: positioning, marketing comms, tooling and AI-supported workflows — in the order that actually moves the business forward.",
  },
];

export default function HowWeWork() {
  return (
    <section
      data-testid="how-we-work-section"
      className="py-20 md:py-28 bg-bg-base"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 md:mb-14">
          <Reveal className="lg:col-span-5">
            <div className="overline">How we work</div>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Three honest<br />
              <em className="font-light text-ink-soft">stages of work.</em>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7 self-end" delay={1}>
            <p className="max-w-xl text-ink/75 leading-relaxed text-base md:text-lg">
              No long retainer rituals. No theatre. We look at your business
              with fresh eyes, design a clearer way of working, and help you
              put it in place.
            </p>
          </Reveal>
        </div>

        {/* Connector line above steps */}
        <div className="relative hidden md:block mb-10">
          <div className="h-px bg-ink/10" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "left" }}
            className="absolute top-0 inset-x-0 h-px bg-brand-red"
          />
          <div className="absolute -top-1.5 left-0 w-3 h-3 rounded-full bg-brand-red" />
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-ink" />
          <div className="absolute -top-1.5 right-0 w-3 h-3 rounded-full border border-ink bg-bg-base" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i} className="relative">
              <div
                data-testid={`process-step-${i + 1}`}
                className="md:border-t-0 border-t border-ink pt-6 md:pt-3 h-full"
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-serif-display text-6xl md:text-7xl text-ink-soft/30">
                    {s.n}
                  </span>
                  <span className="overline text-brand-red">step {i + 1}</span>
                </div>
                <h3 className="mt-3 font-serif-display text-3xl md:text-4xl tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-ink/75 leading-relaxed text-[15px] max-w-sm">
                  {s.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
